<template>
  <div v-if="investigations">
    <span
      v-waypoint="{
        active: true,
        callback: updateShowInvestigationSubSectionText,
        options: {
          rootMargin: '0px',
        },
      }"
    ></span>
    <!-- TODO: margin-top such a jank fix for submenu alignment with other hovered submenu items -->
    <sub-menu
      id="section-submenu"
      nohide="true"
      style="padding-top: 9px !important"
    ></sub-menu>

    <div id="investigations">
      <div class="sm-mx2 flex flex-wrap lazy-container">
        <router-link
          v-for="investigation in investigationsFiltered"
          class="sm-col-12 lg-col-6 mb0 sm-mb3 md-mb3 lg-mb3 relative investigation"
          :class="{
            supported: supportedBy(investigation),
            navigating: redirectId === investigation.id,
          }"
          :to="{
            name: 'Investigation',
            params: { investigationSlug: investigation.slug },
          }"
          :key="investigation.id"
        >
          <div
            class="investigation hover-toggle"
            v-hover-intent:150="playVideo.bind(null, investigation.id)"
            @mouseleave.stop="pauseVideo(investigation.id)"
          >
            <div class="m2">
              <h2 class="investigation-title relative flex flex-auto">
                <div class="flex-auto">
                  <span class="red fs-s"
                    >I.<span v-text="investigation.code"></span
                    >&nbsp;&nbsp;</span
                  >
                  <span
                    class="caps"
                    v-html="preventWidows(investigation.title)"
                  ></span>
                  <span
                    v-if="investigation.fo_status === 'by_fo'"
                    class="fo-badge red caps fs-xs"
                  >
                    Forensic&nbsp;Oceanography
                  </span>
                  <span
                    v-else-if="investigation.fo_status === 'with_fo'"
                    class="fo-badge red caps fs-xs"
                  >
                    With&nbsp;Forensic&nbsp;Oceanography
                  </span>
                  <span
                    v-else-if="supportedBy(investigation)"
                    class="fo-badge red caps fs-xs"
                  >
                    Supported Project
                  </span>
                </div>
              </h2>

              <div class="relative">
                <div class="card-content">
                  <div
                    v-if="showVideo(investigation)"
                    class="video-wrapper relative"
                  >
                    <video
                      class="absolute xs-hide sm-hide md-hide touch-hide"
                      :data-src="investigation.preview_video.link"
                      :ref="'preview-video-' + investigation.id"
                      muted
                      preload="auto"
                    />

                    <img
                      class="video-fallback-image touch-show"
                      :alt="`${investigation.title}`"
                      :data-src="investigation.preview_image.link"
                    />

                    <div class="video-color absolute"></div>
                  </div>

                  <div
                    v-else-if="investigation.hasOwnProperty('feature_image')"
                    class="video-wrapper relative"
                  >
                    <img
                      class="preview-image absolute"
                      :width="investigation.feature_image.width"
                      :height="investigation.feature_image.height"
                      :data-src="investigation.feature_image.link"
                    />
                  </div>

                  <div v-else class="preview-placeholder">
                    <img
                      class="preview-image"
                      :width="investigation.preview_image.width"
                      :height="investigation.preview_image.height"
                      :data-src="investigation.preview_image.link"
                    />
                  </div>

                  <div
                    v-if="
                      investigation.updated_date &&
                      investigation.updated_date !== ''
                    "
                    class="absolute top-0 right-0 z1"
                  >
                    <span class="bg-red text-light h6 p1 block"
                      >Updated
                      {{ formatDate(investigation.updated_date) }}</span
                    >
                  </div>

                  <!-- INVESTIGATION CARD -->
                  <div
                    class="card relative border"
                    v-if="$route.name !== 'Studies'"
                  >
                    <div class="card-row-upper row clearfix flex">
                      <card-block
                        width="30"
                        header="Date<span class='md-hide'> of Incident</span>"
                        class="tabular"
                        :content="incidentDates(investigation)"
                      >
                      </card-block>

                      <card-block
                        width="35"
                        header="Location"
                        :content="investigation.location_title"
                      >
                      </card-block>

                      <card-block
                        width="35"
                        header="Forums"
                        :content="forumTitles(investigation)"
                      >
                      </card-block>
                    </div>

                    <div class="card-row-lower row clearfix flex">
                      <card-block
                        width="30"
                        :header="commissionedByHeading(investigation)"
                        :content="commissionedBy(investigation)"
                      >
                      </card-block>
                      <card-block
                        width="70"
                        type="no-header"
                        header=""
                        :content="clearNewLines(investigation.abstract)"
                      >
                      </card-block>
                    </div>
                  </div>
                  <!-- STUDY CARD -->
                  <div v-else class="card relative border">
                    <div class="card-row-upper row clearfix flex">
                      <card-block
                        width="30"
                        header="Date"
                        class="tabular"
                        :content="incidentDates(investigation)"
                      >
                      </card-block>

                      <card-block
                        width="70"
                        type="no-header"
                        header=""
                        :content="clearNewLines(investigation.abstract)"
                      >
                      </card-block>
                    </div>
                  </div>
                  <a
                    v-if="
                      supportedBy(investigation) &&
                      investigation.supported_project_link
                    "
                    :href="getLink(investigation)"
                    target="_blank"
                    class="md-hide absolute top-0 left-0 bg-red-opacity text-light z1 p2"
                    >External Link ↗</a
                  >
                </div>
              </div>
            </div>
          </div>
        </router-link>
      </div>
    </div>

    <div
      id="info-box"
      class="fs-s z1 p2 border col-8 sm-col-5 lg-col-4"
      v-if="infoBox"
      :class="{ open: infoBoxOpen }"
      @click="infoBoxOpen = !infoBoxOpen"
    >
      <h3 class="caps m0 flex justify-between">
        <span v-html="infoBox.title"></span>
        <span>⤵</span>
      </h3>
      <div class="relative">
        <div class="description" v-html="infoBox.content"></div>
        <div class="description-gradient col-12 absolute"></div>
      </div>
    </div>
  </div>
</template>

<script>
import { debounce } from "lodash";
import { mapState, mapActions } from "vuex";
import {
  commissionedBy,
  commissionedByHeading,
  incidentDates,
  formatDate,
  getPreviewVideoFile,
  isStudy,
  sortInvestigations,
} from "@/utils";
import SubMenu from "@/components/SubMenu";
import CardBlock from "@/components/CardBlock";

export default {
  name: "Investigations",

  data() {
    return {
      infoBoxOpen: false,
      lazyContainer: this.lazyLoad({
        elements_selector: ".lazy-container img",
      }),
    };
  },

  metaInfo() {
    let title = "Investigations";
    if (this.$route.name === "Investigation Category") {
      title = `${this.unSlug(this.$route.params.slug)}`;
    } else if (this.$route.name === "Investigation Location") {
      title = `${this.unSlug(this.$route.params.slug)}`;
    } else if (this.$route.name === "Investigation Methodology") {
      title = `${this.unSlug(this.$route.params.slug)}`;
    }

    // TODO: Oh no, it's not functional
    title = this.titleCase(title);

    return {
      title: title,
      meta: [
        { name: "og:title", content: title },
        { name: "twitter:title", content: title },
        { name: "og:image", content: "/fa-share-lg.png" },
        { name: "twitter:image", content: "/fa-share-lg.png" },
        {
          name: "og:url",
          content: `${window.location.origin}/${this.$route.fullPath}`,
        },
        { name: "og:type", content: "website" },
        { name: "og:description", content: this.siteDescription() },
      ],
    };
  },

  mounted: function () {
    this.$nextTick(function () {
      this.lazyContainer.update();
      this.equalizeHeights();
    });
    window.onresize = debounce(() => {
      this.equalizeHeights();
    }, 100);
  },

  components: {
    CardBlock,
    SubMenu,
  },

  computed: {
    ...mapState(["investigations", "investigationCategories", "methodologies"]),
    investigationsFiltered() {
      let filtered;
      if (
        this.investigations &&
        this.$route.name === "Investigation Category"
      ) {
        filtered = this.investigations.filter((i) => {
          if (i.investigation_area) {
            return i.investigation_area.some(
              (ia) => ia.slug === this.$route.params.slug
            );
          } else {
            return false;
          }
        });
      } else if (
        this.investigations &&
        this.$route.name === "Investigation Location"
      ) {
        filtered = this.investigations.filter((i) => {
          if (
            i.investigation_location.some(
              (e) => e.slug === this.$route.params.slug
            )
          ) {
            return true;
          }
        });
      } else if (
        this.investigations &&
        this.methodologies &&
        this.$route.name === "Investigation Methodology"
      ) {
        filtered = this.investigations.filter((i) => {
          if (i.methodologies) {
            if (
              i.methodologies.some(
                (e) => e.post_name === this.$route.params.slug
              )
            ) {
              return true;
            } else {
              return false;
            }
          } else {
            return false;
          }
        });
      } else if (this.investigations && this.$route.name === "Studies") {
        filtered = this.$store.getters.allInvestigations
          .filter(isStudy)
          .sort(sortInvestigations);
      } else {
        filtered = this.investigations;
      }

      // Sort the filtered results
      if (!filtered) return [];

      return [...filtered].sort((a, b) => {
        // If both items have updated_date, sort by that
        if (a.updated_date && b.updated_date) {
          return new Date(b.updated_date) - new Date(a.updated_date);
        } else if (a.updated_date && !b.updated_date) {
          return new Date(b.publication_date) - new Date(a.updated_date);
        } else if (!a.updated_date && b.updated_date) {
          return new Date(b.updated_date) - new Date(a.publication_date);
        } else {
          return new Date(b.publication_date) - new Date(a.publication_date);
        }
      });
    },
    infoBox() {
      if (this.$route.meta.infoBox) {
        if (this.$route.meta.infoBoxType === "methodology") {
          if (this.methodologies) {
            let item = this.methodologies.find(
              (x) => x.slug === this.$route.params.slug
            );
            return {
              title: item.title.rendered,
              content: item.acf.description,
            };
          } else {
            return false;
          }
        } else if (this.$route.meta.infoBoxType === "area") {
          let item = this.investigationCategories.find(
            (x) => x.slug === this.$route.params.slug
          );
          if (item.description) {
            return {
              title: item.name,
              content: item.description,
            };
          } else {
            return false;
          }
        } else {
          return false;
        }
      } else {
        return false;
      }
    },
  },

  methods: {
    ...mapActions(["setShowInvestigationSubSectionText"]),
    commissionedBy,
    commissionedByHeading,
    incidentDates,
    formatDate,
    getPreviewVideoFile,
    updateShowInvestigationSubSectionText({ going }) {
      if (going === "out") {
        this.setShowInvestigationSubSectionText(true);
      } else {
        this.setShowInvestigationSubSectionText(false);
      }
    },
    supportedBy(investigation) {
      return (
        this.commissionedByHeading(investigation) ===
        "A Forensis-supported project by"
      );
    },
    getLink(investigation) {
      if (!this.supportedBy(investigation)) {
        return `/investigation/${investigation.slug}`;
      } else {
        console.log(investigation.supported_project_link);
        return investigation.supported_project_link;
      }
    },
    navigateTo(investigation) {
      if (!this.supportedBy(investigation)) {
        this.$router.push({
          name: "Investigation",
          params: { investigationSlug: investigation.slug },
        });
      } else {
        window.open(investigation.supported_project_link, "_blank");
      }
    },
    showVideo(investigation) {
      return !this.isMobileWidth() && investigation.preview_video;
    },
    playVideo(index) {
      let el = this.$refs["preview-video-" + index][0];
      if (!el.hasAttribute("src")) {
        el.setAttribute("src", el.getAttribute("data-src"));
      }
      this.$refs["preview-video-" + index][0].play();
    },
    pauseVideo(index) {
      this.$refs["preview-video-" + index][0].pause();
    },
    equalizeHeights() {
      let items = Array.prototype.slice
        .call(document.querySelectorAll(".investigation"))
        .reduce(function (result, value, index, array) {
          if (index % 2 === 0) result.push(array.slice(index, index + 2));
          return result;
        }, []);

      let classes = [
        ".investigation-title",
        ".card-row-upper",
        ".card-row-lower",
      ];

      items.map((x) => {
        if (x.length === 2) {
          classes.map((className) => {
            let a = x[0].querySelector(className);
            let b = x[1].querySelector(className);
            a.removeAttribute("style");
            b.removeAttribute("style");
            let maxHeight = Math.max.apply(Math, [
              a.clientHeight,
              b.clientHeight,
            ]);
            a.style.height = maxHeight + "px";
            b.style.height = maxHeight + "px";
          });
        }
      });
    },
    // TODO: DUPE DRY UP
    updateLazy() {
      this.$nextTick(function () {
        this.lazyContainer.update();
      });
    },
  },
  watch: {
    investigations() {
      this.$nextTick(function () {
        this.equalizeHeights();
        this.updateLazy();
      });
    },
    $route() {
      this.$nextTick(function () {
        this.updateLazy();
      });
    },
  },
};
</script>

<style lang="css" scoped>
#investigations {
  margin-top: -1.25em;
  margin-bottom: 10em;
}
.video-wrapper {
  padding-top: 25px;
  width: 100%;
  padding-top: 56.1%;
  background: var(--load-bg);
  overflow: hidden;
}
.investigation {
  &:hover {
    h2.investigation-title {
      color: var(--red);
    }
    img,
    video {
      filter: brightness(100%) contrast(100%);
      opacity: 1;
    }
  }
}

.abstract {
  display: none;
  background: var(--col-light);
  top: 0;
  left: 30%;
  width: calc(70% - var(--space-2) * 2);
  height: calc(100% - var(--space-2));
}

video,
img {
  top: 0;
  /*opacity: 0.2;*/
  transition: filter 0.3s ease-out;
  filter: brightness(110%) contrast(80%);
  width: 100%;
}

video {
  z-index: 1;
}

img {
  object-fit: cover;
  height: 100%;
}

.video-color {
  top: 0;
  width: 100%;
  height: 98.5%;
  background: var(--col-light);
  mix-blend-mode: multiply;
}

.video-fallback-image {
  object-fit: fill;
  position: absolute;
  top: 0;
  left: 0;
  pointer-events: none;
  /* TODO: Slightly jank fix for tiny height discrepancy between fallback and vid */
  height: calc(100% + 1px);
}

.preview-placeholder {
  overflow: hidden;
  position: relative;
  width: 100%;
  height: 40vh !important;

  img {
    position: absolute;
    object-fit: cover;
    top: 0;
  }
}

.preview-image + .card {
  margin-top: -0.3em;
}

.fo-badge {
  padding-left: 0.5em;
}

#info-box {
  max-width: 600px;
  position: fixed;
  bottom: 1em;
  right: 1em;
  opacity: 0.8;
  background-color: var(--red);
  color: var(--col-light);
  transition: all 0.3s ease-out;
  cursor: pointer;

  &:hover {
    opacity: 1;
  }

  .description {
    -webkit-overflow-scrolling: touch;
    max-height: 4.5em;
    overflow: hidden;
    transition: all 0.3s ease-out;

    >>> p:last-child {
      padding-bottom: 0;
      margin-bottom: 0;
    }

    >>> a {
      color: var(--col-light);
      text-decoration: underline;
    }
  }

  .description-gradient {
    pointer-events: none;
    transition: all 0.3s ease-out;
    height: 2em;
    margin-top: -2em;
    background-image: linear-gradient(
      to bottom,
      rgba(var(--red-val), 0),
      rgba(var(--red-val), 1)
    );
  }

  &.open {
    opacity: 1;

    .description {
      overflow-y: scroll;
      max-height: 50vh;
    }
    .description-gradient {
      opacity: 0;
    }
  }
}

@media (--phone) {
  #investigations {
    margin-top: 0;
    margin-bottom: 0;
  }
}
</style>
